import type {
    ConnectorItemDeclaration,
    LabelTemplate,
} from '@toolproof-core/visualization/_contract/declaration';
import type { Shape } from '@toolproof-core/visualization/_contract/shape';

import { GEOMETRY_SCALE } from '@/workflow/_data/config/fragments/geometryScale';
import type {
    BaseConfig,
    GeometryScale,
} from '@/workflow/_contract/config';

type ScalableLabel = LabelTemplate;
const ABOVE_ITEM_LABEL_REFERENCE_HALF_EXTENT = 0.35;
const ABOVE_ITEM_LABEL_MAX_SIZE_SCALE = 1.5;

function validateScale(name: string, value: number): void {
    if (!Number.isFinite(value) || value <= 0) {
        throw new Error(`Workflow geometry scale '${name}' must be finite and greater than zero.`);
    }
}

function validateGeometryScale(scale: GeometryScale): void {
    validateScale('resourceTypes.ringRadiusFromRoot', scale.multipliers.resourceTypes.ringRadiusFromRoot);
    validateScale('resourceTypes.sphereRadiusFromRing', scale.multipliers.resourceTypes.sphereRadiusFromRing);
    validateScale('roles.ringRadiusFromResourceTypeSphere', scale.multipliers.roles.ringRadiusFromResourceTypeSphere);
    validateScale('roles.sphereRadiusFromRing', scale.multipliers.roles.sphereRadiusFromRing);
    validateScale('toolSteps.ringGapFromResourceTypeRing', scale.multipliers.toolSteps.ringGapFromResourceTypeRing);
    validateScale('toolSteps.tubeRadiusFromRoot', scale.multipliers.toolSteps.tubeRadiusFromRoot);
}

function scaleTuple(
    tuple: readonly [number, number, number],
    scale: number,
): [number, number, number] {
    return [tuple[0] * scale, tuple[1] * scale, tuple[2] * scale];
}

function scalePlacement(
    placement: NonNullable<LabelTemplate['placement']>,
    scale: number,
): NonNullable<LabelTemplate['placement']> {
    if (placement.placementKind === 'anchor') {
        return placement.localOffset === undefined
            ? placement
            : { ...placement, localOffset: scaleTuple(placement.localOffset, scale) };
    }
    return placement.offset === undefined
        ? placement
        : { ...placement, offset: scaleTuple(placement.offset, scale) };
}

function scaleLabel<TLabel extends ScalableLabel | undefined>(
    label: TLabel,
    scale: number,
    itemSizeScale = 1,
): TLabel {
    if (label === undefined) return label;
    const totalScale = scale * itemSizeScale;
    return {
        ...label,
        ...(label.fontSize === undefined ? {} : { fontSize: label.fontSize * totalScale }),
        ...(label.padding === undefined ? {} : { padding: label.padding * totalScale }),
        ...(label.placement === undefined ? {} : { placement: scalePlacement(label.placement, scale) }),
        ...(label.flag && typeof label.flag === 'object'
            ? {
                flag: {
                    ...label.flag,
                    postOffset: scaleTuple(label.flag.postOffset, totalScale),
                    ...(label.flag.prongOffsets === undefined ? {} : {
                        prongOffsets: label.flag.prongOffsets.map(offset => scaleTuple(offset, totalScale)),
                    }),
                },
            }
            : {}),
    } as TLabel;
}

function toAboveItemLabelSizeScale(
    shape: Shape | undefined,
    geometryScale: number,
    defaultHalfExtent = 0.6,
): number {
    const halfExtent = shape?.shapeKind === 'sphere'
        ? shape.radius
        : shape?.shapeKind === 'panel'
            ? shape.height / 2
            : shape === undefined ? defaultHalfExtent : null;
    if (halfExtent === null) return 1;
    return Math.min(
        halfExtent * geometryScale / ABOVE_ITEM_LABEL_REFERENCE_HALF_EXTENT,
        ABOVE_ITEM_LABEL_MAX_SIZE_SCALE,
    );
}

function scaleConnectorDeclaration<TDeclaration extends ConnectorItemDeclaration>(
    declaration: TDeclaration,
    scale: number,
): TDeclaration {
    return {
        ...declaration,
        ...(declaration.path === undefined
            ? {}
            : {
                path: {
                    ...declaration.path,
                    ...(declaration.path.bracketOffset === undefined
                        ? {}
                        : { bracketOffset: declaration.path.bracketOffset * scale }),
                    ...(declaration.path.curveMinOffset === undefined
                        ? {}
                        : { curveMinOffset: declaration.path.curveMinOffset * scale }),
                },
            }),
    } as TDeclaration;
}

export function deriveEffectiveGeometryConfig(
    config: BaseConfig,
): BaseConfig {
    validateGeometryScale(config.geometryScale);
    validateScale(
        'workflowStateEntries.panels.panelWidthFromRoot',
        config.geometryScale.workflowStateEntries.panels.panelWidthFromRoot,
    );
    validateScale(
        'workflowStateEntries.panels.panelHeightFromRoot',
        config.geometryScale.workflowStateEntries.panels.panelHeightFromRoot,
    );
    validateScale(
        'workflowStateEntries.panels.panelDepthFromRoot',
        config.geometryScale.workflowStateEntries.panels.panelDepthFromRoot,
    );
    validateScale(
        'workflowStateEntries.wheels.bandGeometry',
        config.geometryScale.workflowStateEntries.wheels.bandGeometry,
    );

    const tools = config.domainPresentation.entities.tools;
    const rootRadius = tools.layout.outerRadius;
    validateScale('rootRadius', rootRadius);
    const rootScale = rootRadius / GEOMETRY_SCALE.rootRadius;
    const panelWidthScale = rootScale
        * config.geometryScale.workflowStateEntries.panels.panelWidthFromRoot
        / GEOMETRY_SCALE.workflowStateEntries.panels.panelWidthFromRoot;
    const panelHeightScale = rootScale
        * config.geometryScale.workflowStateEntries.panels.panelHeightFromRoot
        / GEOMETRY_SCALE.workflowStateEntries.panels.panelHeightFromRoot;
    const panelDepthScale = rootScale
        * config.geometryScale.workflowStateEntries.panels.panelDepthFromRoot
        / GEOMETRY_SCALE.workflowStateEntries.panels.panelDepthFromRoot;
    const resourceTypes = config.domainPresentation.entities.resourceTypes;
    const roles = config.domainPresentation.entities.roles;
    const toolSteps = config.domainPresentation.entities.toolSteps;
    const toolRingOuterRadius = rootRadius;
    const resourceTypeRingRadius = toolRingOuterRadius
        * config.geometryScale.multipliers.resourceTypes.ringRadiusFromRoot;
    const resourceTypeSphereRadius = resourceTypeRingRadius
        * config.geometryScale.multipliers.resourceTypes.sphereRadiusFromRing;
    const roleRingRadius = resourceTypeSphereRadius
        * config.geometryScale.multipliers.roles.ringRadiusFromResourceTypeSphere;
    const roleSphereRadius = roleRingRadius
        * config.geometryScale.multipliers.roles.sphereRadiusFromRing;
    const toolStepTubeRadius = toolRingOuterRadius
        * config.geometryScale.multipliers.toolSteps.tubeRadiusFromRoot;
    const { cases, macroSteps } = config.domainPresentation.entities;
    const workflowStateEntries = config.domainPresentation.entities.workflowStateEntries;
    const cameraPose = config.spaceRenderOptions?.camera?.pose;

    return {
        ...config,
        spaceRenderOptions: {
            ...config.spaceRenderOptions,
            ...(cameraPose === undefined
                ? {}
                : {
                    camera: {
                        ...config.spaceRenderOptions?.camera,
                        pose: {
                            ...cameraPose,
                            position: scaleTuple(cameraPose.position, rootScale),
                            target: scaleTuple(cameraPose.target, rootScale),
                        },
                    },
                }),
        },
        domainPresentation: {
            ...config.domainPresentation,
            entities: {
                ...config.domainPresentation.entities,
                cases: {
                    ...cases,
                    bands: { ...cases.bands, gutter: cases.bands.gutter * rootScale, minWidth: cases.bands.minWidth * rootScale },
                    tori: { ...cases.tori, minimumClearance: cases.tori.minimumClearance * rootScale },
                },
                macroSteps: {
                    ...macroSteps,
                    tori: { ...macroSteps.tori, minimumClearance: macroSteps.tori.minimumClearance * rootScale },
                },
                tools: {
                    ...tools,
                    layout: {
                        ...tools.layout,
                        outerRadius: toolRingOuterRadius,
                        ...(tools.layout.outerThickness === undefined
                            ? {} : { outerThickness: tools.layout.outerThickness * rootScale }),
                        ...(tools.layout.innerRadius === undefined
                            ? {}
                            : { innerRadius: tools.layout.innerRadius * rootScale }),
                    },
                    item: tools.item === undefined
                        ? undefined
                        : {
                            ...tools.item,
                            label: tools.item.label === undefined
                                ? undefined
                                : {
                                    ...tools.item.label,
                                    onItem: scaleLabel(tools.item.label.onItem, rootScale),
                                    backItem: scaleLabel(tools.item.label.backItem, rootScale),
                                },
                        },
                    labelLayoutPolicy: tools.labelLayoutPolicy === undefined
                        ? undefined
                        : {
                            ...tools.labelLayoutPolicy,
                            ...(tools.labelLayoutPolicy.minFontSize === undefined
                                ? {}
                                : { minFontSize: tools.labelLayoutPolicy.minFontSize * rootScale }),
                            ...(tools.labelLayoutPolicy.maxFontSize === undefined
                                ? {}
                                : { maxFontSize: tools.labelLayoutPolicy.maxFontSize * rootScale }),
                            ...(tools.labelLayoutPolicy.yOffset === undefined
                                ? {}
                                : { yOffset: tools.labelLayoutPolicy.yOffset * rootScale }),
                        },
            },
            resourceTypes: {
                ...resourceTypes,
                layout: {
                    ...resourceTypes.layout,
                    radius: resourceTypeRingRadius,
                },
                ringGuide: resourceTypes.ringGuide === false || resourceTypes.ringGuide === undefined
                    ? resourceTypes.ringGuide
                    : {
                        ...resourceTypes.ringGuide,
                        ...(resourceTypes.ringGuide.y === undefined
                            ? {}
                            : { y: resourceTypes.ringGuide.y * rootScale }),
                        ...(resourceTypes.ringGuide.width === undefined
                            ? {}
                            : { width: resourceTypes.ringGuide.width * rootScale }),
                    },
                item: resourceTypes.item === undefined
                    ? undefined
                    : {
                        ...resourceTypes.item,
                        shape: resourceTypes.item.shape === undefined
                            ? undefined
                            : {
                                ...resourceTypes.item.shape,
                                radius: resourceTypeSphereRadius,
                            },
                        label: resourceTypes.item.label === undefined
                            ? undefined
                            : {
                                ...resourceTypes.item.label,
                                aboveItem: scaleLabel(
                                    resourceTypes.item.label.aboveItem,
                                    rootScale,
                                    toAboveItemLabelSizeScale(resourceTypes.item.shape, 1),
                                ),
                            },
                    },
            },
            roles: {
                ...Object.fromEntries(Object.entries(roles).map(([direction, declaration]) => [
                    direction,
                    {
                        ...declaration,
                        layout: {
                            ...declaration.layout,
                            radius: roleRingRadius,
                        },
                        crescentGuide: declaration.crescentGuide === false || declaration.crescentGuide === undefined
                            ? declaration.crescentGuide
                            : {
                                ...declaration.crescentGuide,
                                ...(declaration.crescentGuide.width === undefined
                                    ? {}
                                    : { width: declaration.crescentGuide.width * rootScale }),
                            },
                        item: declaration.item === undefined
                            ? undefined
                            : {
                                ...declaration.item,
                                shape: declaration.item.shape === undefined
                                    ? undefined
                                    : {
                                        ...declaration.item.shape,
                                        radius: roleSphereRadius,
                                    },
                                label: declaration.item.label === undefined
                                    ? undefined
                                    : {
                                        ...declaration.item.label,
                                        aboveItem: scaleLabel(
                                            declaration.item.label.aboveItem,
                                            rootScale,
                                            toAboveItemLabelSizeScale(declaration.item.shape, 1),
                                        ),
                                    },
                            },
                    },
                ])) as typeof roles,
            },
            toolSteps: {
                ...toolSteps,
                slotBands: {
                    ...toolSteps.slotBands,
                    width: toolSteps.slotBands.width * rootScale,
                    thickness: toolSteps.slotBands.thickness * rootScale,
                    ...(toolSteps.slotBands.teeth ? { teeth: {
                        count: toolSteps.slotBands.teeth.count,
                        depth: toolSteps.slotBands.teeth.depth * rootScale,
                    } } : {}),
                },
                tubeRadius: toolStepTubeRadius,
                item: {
                    ...toolSteps.item,
                    label: toolSteps.item.label === undefined
                        ? undefined
                        : {
                            ...toolSteps.item.label,
                            aboveItem: scaleLabel(
                                toolSteps.item.label.aboveItem,
                                rootScale,
                                toAboveItemLabelSizeScale(
                                    toolSteps.item.shape,
                                    1,
                                ),
                            ),
                        },
                },
            },
            workflowStateEntries: {
                ...workflowStateEntries,
                item: {
                    ...workflowStateEntries.item,
                    shape: {
                        ...workflowStateEntries.item.shape,
                        width: workflowStateEntries.item.shape.width * panelWidthScale,
                        height: workflowStateEntries.item.shape.height * panelHeightScale,
                        ...(workflowStateEntries.item.shape.depth === undefined
                            ? {}
                            : {
                                depth: workflowStateEntries.item.shape.depth * panelDepthScale,
                            }),
                    },
                    label: workflowStateEntries.item.label === undefined
                        ? undefined
                        : {
                            ...workflowStateEntries.item.label,
                            aboveItem: scaleLabel(
                                workflowStateEntries.item.label.aboveItem,
                                rootScale,
                                toAboveItemLabelSizeScale(
                                    workflowStateEntries.item.shape,
                                    panelHeightScale / rootScale,
                                ),
                            ),
                            onItem: scaleLabel(
                                workflowStateEntries.item.label.onItem,
                                rootScale,
                            ),
                        },
                },
            },
            },
            connectors: {
                ...config.domainPresentation.connectors,
                toolRole: {
                    ...scaleConnectorDeclaration(
                        config.domainPresentation.connectors.toolRole,
                        rootScale,
                    ),
                },
                resourceTypeRadial: {
                    ...scaleConnectorDeclaration(
                        config.domainPresentation.connectors.resourceTypeRadial,
                        rootScale,
                    ),
                },
                workflowStateEntry: {
                    ...scaleConnectorDeclaration(
                        config.domainPresentation.connectors.workflowStateEntry,
                        rootScale,
                    ),
                },
                macroStep: {
                    ...scaleConnectorDeclaration(
                        config.domainPresentation.connectors.macroStep,
                        rootScale,
                    ),
                    radialOffset: config.domainPresentation.connectors.macroStep.radialOffset * rootScale,
                    ...(config.domainPresentation.connectors.macroStep.casePair === undefined ? {} : {
                        casePair: {
                            ...config.domainPresentation.connectors.macroStep.casePair,
                            curveMinOffset: config.domainPresentation.connectors.macroStep.casePair.curveMinOffset * rootScale,
                        },
                    }),
                },
            },
        },
    };
}